/**
 * Feature Flags — controls localStorage→API migration per service.
 *
 * Each flag can be overridden via VITE_FF_* environment variables.
 * Defaults: migrated services use DUAL_WRITE, unmigrated use LS_ONLY.
 */

export type MigrationMode = 'LS_ONLY' | 'DUAL_WRITE' | 'API_ONLY';

interface FeatureFlag {
  key: string;
  envVar: string;
  default: MigrationMode;
  description: string;
}

export const FEATURE_FLAGS = {
  profiles: {
    key: 'profiles',
    envVar: 'VITE_FF_PROFILES',
    default: 'DUAL_WRITE' as MigrationMode,
    description: 'User profile sync (personalization, diet prefs)',
  },
  onboarding: {
    key: 'onboarding',
    envVar: 'VITE_FF_ONBOARDING',
    default: 'DUAL_WRITE' as MigrationMode,
    description: 'Onboarding data persistence',
  },
  groceries: {
    key: 'groceries',
    envVar: 'VITE_FF_GROCERIES',
    default: 'DUAL_WRITE' as MigrationMode,
    description: 'Grocery list sync',
  },
  mealPlans: {
    key: 'mealPlans',
    envVar: 'VITE_FF_MEAL_PLANS',
    default: 'DUAL_WRITE' as MigrationMode,
    description: 'Meal plan storage sync',
  },
  streaks: {
    key: 'streaks',
    envVar: 'VITE_FF_STREAKS',
    default: 'DUAL_WRITE' as MigrationMode,
    description: 'Streak tracking sync',
  },
  household: {
    key: 'household',
    envVar: 'VITE_FF_HOUSEHOLD',
    default: 'DUAL_WRITE' as MigrationMode,
    description: 'Household context sync',
  },
} as const;

type FlagKey = keyof typeof FEATURE_FLAGS;

function getMode(flag: FeatureFlag): MigrationMode {
  const envValue = import.meta.env[flag.envVar];
  if (envValue && ['LS_ONLY', 'DUAL_WRITE', 'API_ONLY'].includes(envValue)) {
    return envValue as MigrationMode;
  }
  return flag.default;
}

/** True when API calls should be made (DUAL_WRITE or API_ONLY) */
export function isApiEnabled(flag: FeatureFlag): boolean {
  const mode = getMode(flag);
  return mode === 'DUAL_WRITE' || mode === 'API_ONLY';
}

/** True when localStorage reads/writes should happen (LS_ONLY or DUAL_WRITE) */
export function isLocalStorageEnabled(flag: FeatureFlag): boolean {
  const mode = getMode(flag);
  return mode === 'LS_ONLY' || mode === 'DUAL_WRITE';
}

/** Get all flag states for admin panel display */
export function getAllFlagStates(): Array<{ key: string; mode: MigrationMode; description: string }> {
  return Object.values(FEATURE_FLAGS).map(flag => ({
    key: flag.key,
    mode: getMode(flag),
    description: flag.description,
  }));
}
